import React, { useState } from "react";
import { Box, Button, Card, CardContent, Typography } from "@mui/material";
import { ListItemIcon, StatusGlyph, TextField } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { useTranslation } from "react-i18next";
import { InvoiceStatus } from "enums/InvoiceStatus";
import contactHttpClient from "utils/contactHttpClient";

const CENTERED_SX = { alignItems: "center", textAlign: "center" };

interface EstimateActionBoxProps {
  invoiceStatus: InvoiceStatus;
  invoiceId: string;
  recipientId: string;
  fetchInvoice: (invoiceId: string, recipientId: string) => void;
  setApproveOrRejectLoading: (loading: boolean) => void;
}

const EstimateActionBox: React.FC<EstimateActionBoxProps> = ({
  invoiceStatus,
  invoiceId: invoiceIdFromUrl,
  recipientId: recipientIdFromUrl,
  fetchInvoice,
  setApproveOrRejectLoading,
}) => {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const [noteTouched, setNoteTouched] = useState(false);
  const [rejecting, setRejecting] = useState(false);

  const actionInvoiceEstimate = async (
    action: InvoiceStatus.ESTIMATE_APPROVED | InvoiceStatus.ESTIMATE_REJECTED,
    note?: string,
  ) => {
    setApproveOrRejectLoading(true);
    try {
      await contactHttpClient.post(
        `/invoice/contact_action/${action}/${invoiceIdFromUrl}/${recipientIdFromUrl}`,
        {
          invoiceIdFromUrl,
          recipientId: recipientIdFromUrl,
          note,
        },
      );
      invoiceIdFromUrl &&
        recipientIdFromUrl &&
        fetchInvoice(invoiceIdFromUrl, recipientIdFromUrl);
    } catch (error) {
      console.error("Error actioning invoice:", error);
    } finally {
      setApproveOrRejectLoading(false);
    }
  };

  if (
    [InvoiceStatus.ESTIMATE_APPROVED, InvoiceStatus.ESTIMATE_REJECTED].includes(
      invoiceStatus,
    )
  ) {
    const approved = invoiceStatus === InvoiceStatus.ESTIMATE_APPROVED;

    return (
      <Card>
        <CardContent>
          <Stack gap="var(--space-5)" sx={CENTERED_SX}>
            <ListItemIcon
              wrapper={approved ? "success" : "danger"}
              sx={{ minWidth: 0 }}
            >
              <StatusGlyph
                status={approved ? "estimate approved" : "estimate rejected"}
                size={20}
              />
            </ListItemIcon>
            <Stack gap="var(--space-2)" sx={CENTERED_SX}>
              <Typography variant="h3">
                {approved ? t("Estimate Approved") : t("Estimate Rejected")}
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "var(--color-text-secondary)" }}
              >
                {approved
                  ? t("InvoiceEstimateApprovalDescription")
                  : t("InvoiceEstimateRejectionDescription")}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Stack gap="var(--space-5)" sx={CENTERED_SX}>
          <Stack gap="var(--space-2)" sx={CENTERED_SX}>
            <Typography variant="h3">{t("This is an estimate")}</Typography>
            <Typography
              variant="body1"
              sx={{ color: "var(--color-text-secondary)" }}
            >
              {t("InvoiceEstimateDescription")}
            </Typography>
          </Stack>

          {rejecting && (
            <Box sx={{ width: "100%", textAlign: "left" }}>
              <TextField
                id="estimate-rejection-reason"
                label={t("Reason for rejection")}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                onBlur={() => setNoteTouched(true)}
                required
                multiline
                rows={3}
                fullWidth
                autoFocus
                errorText={
                  noteTouched && !note.trim()
                    ? t("Reason is required")
                    : undefined
                }
              />
            </Box>
          )}

          {/* The decision last, as every other action row on these pages. */}
          <Inline justify="center" gap="var(--space-3)">
            {!rejecting ? (
              <>
                <Button
                  variant="danger-outlined"
                  onClick={() => setRejecting(true)}
                >
                  {t("Reject")}
                </Button>
                <Button
                  variant="primary-filled"
                  onClick={() => {
                    actionInvoiceEstimate(
                      InvoiceStatus.ESTIMATE_APPROVED,
                      note,
                    );
                  }}
                >
                  {t("Approve")}
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="default-outlined"
                  onClick={() => {
                    setRejecting(false);
                    setNote("");
                    setNoteTouched(false);
                  }}
                >
                  {t("Cancel")}
                </Button>
                <Button
                  variant="danger-filled"
                  onClick={() => {
                    actionInvoiceEstimate(
                      InvoiceStatus.ESTIMATE_REJECTED,
                      note,
                    );
                  }}
                  disabled={!note.trim()}
                >
                  {t("Submit Rejection")}
                </Button>
              </>
            )}
          </Inline>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default EstimateActionBox;
